import React, { useEffect, useState, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

/**
 * Two-factor authentication settings panel.
 *
 * Self-contained: fetches status on mount, handles enable / disable /
 * regenerate-backup-codes flows internally. Drop into the Security
 * & Permissions tab as `<TwoFactorSetup authToken={authToken} />`.
 *
 * Modes:
 *   'idle'              — show current status + primary action button
 *   'setup'             — show QR + manual key + 6-digit input
 *   'showBackupCodes'   — one-time display of backup codes after a
 *                         successful enable / regenerate
 *   'disable'           — confirm-with-password+code form
 *   'regenerate'        — same shape as disable; replaces backup codes
 */
export default function TwoFactorSetup({ authToken, onEnabled = null }) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('idle');

  // Setup-flow state
  const [setupData, setSetupData] = useState(null);    // { qrDataUrl, secret, account, issuer }
  const [setupCode, setSetupCode] = useState('');
  const [setupBusy, setSetupBusy] = useState(false);

  // One-time backup-codes display
  const [backupCodes, setBackupCodes] = useState(null);

  // Disable / regenerate form state
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [confirmBusy, setConfirmBusy] = useState(false);

  const auth = useCallback((url, init = {}) => {
    return fetch(`${API_BASE}${url}`, {
      ...init,
      headers: {
        ...(init.headers || {}),
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    });
  }, [authToken]);

  const refreshStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await auth('/api/auth/2fa/status');
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `Failed to load (${r.status})`);
      setStatus(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  const startSetup = async () => {
    setError(null);
    setSetupBusy(true);
    try {
      const r = await auth('/api/auth/2fa/setup-init', { method: 'POST', body: '{}' });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `Setup failed (${r.status})`);
      setSetupData(data);
      setSetupCode('');
      setMode('setup');
    } catch (err) {
      setError(err.message);
    } finally {
      setSetupBusy(false);
    }
  };

  const verifySetup = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError(null);
    setSetupBusy(true);
    try {
      const r = await auth('/api/auth/2fa/setup-verify', {
        method: 'POST',
        body: JSON.stringify({ code: setupCode.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `Verify failed (${r.status})`);
      setBackupCodes(data.backupCodes || []);
      setSetupData(null);
      setSetupCode('');
      setMode('showBackupCodes');
      // Refresh status so the parent panel reflects "enabled" once
      // backup codes are dismissed.
      refreshStatus();
      // Optional parent hook (2026-08-11, MFA enrollment gate): the
      // gate needs to know enrollment succeeded so it can surface its
      // "sign in again to continue" step — a restricted session stays
      // restricted until a fresh login.
      if (typeof onEnabled === 'function') onEnabled();
    } catch (err) {
      setError(err.message);
    } finally {
      setSetupBusy(false);
    }
  };

  const submitConfirm = async (e, kind) => {
    if (e && e.preventDefault) e.preventDefault();
    setError(null);
    setConfirmBusy(true);
    try {
      const url = kind === 'disable'
        ? '/api/auth/2fa/disable'
        : '/api/auth/2fa/regenerate-backup-codes';
      const r = await auth(url, {
        method: 'POST',
        body: JSON.stringify({
          currentPassword: confirmPassword,
          code: confirmCode.trim(),
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `Action failed (${r.status})`);

      setConfirmPassword('');
      setConfirmCode('');
      if (kind === 'disable') {
        setMode('idle');
      } else {
        setBackupCodes(data.backupCodes || []);
        setMode('showBackupCodes');
      }
      refreshStatus();
    } catch (err) {
      setError(err.message);
    } finally {
      setConfirmBusy(false);
    }
  };

  const dismissBackupCodes = () => {
    setBackupCodes(null);
    setMode('idle');
  };

  if (loading) {
    return <div style={panelStyle}>Loading 2FA status…</div>;
  }

  return (
    <div style={panelStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--room-text-strong, #0f172a)' }}>
            Two-factor authentication
          </h3>
          <p style={{ margin: '4px 0 0', color: 'var(--room-text-muted, #64748b)', fontSize: 13 }}>
            {status?.enabled
              ? 'Active — a 6-digit code from your authenticator is required at every sign-in.'
              : 'Add a second step at sign-in. Recommended for every admin and seller.'}
          </p>
        </div>
        <span style={{
          padding: '4px 12px', borderRadius: 100,
          fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: status?.enabled ? '#065f46' : 'var(--room-text-body, #475569)',
          background: status?.enabled ? '#d1fae5' : 'var(--room-border-soft, #f1f5f9)',
        }}>
          {status?.enabled ? 'Enabled' : 'Disabled'}
        </span>
      </div>

      {error && (
        <div style={errorStyle}>{error}</div>
      )}

      {mode === 'idle' && !status?.enabled && (
        <button type="button" onClick={startSetup} disabled={setupBusy} style={primaryBtn}>
          {setupBusy ? 'Generating…' : 'Enable two-factor authentication'}
        </button>
      )}

      {mode === 'idle' && status?.enabled && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {typeof status.backupCodesRemaining === 'number' && (
            <div style={{ fontSize: 13, color: 'var(--room-text-body, #475569)' }}>
              <strong style={{ color: 'var(--room-text-strong, #0f172a)' }}>{status.backupCodesRemaining}</strong> backup code
              {status.backupCodesRemaining === 1 ? '' : 's'} remaining.
              {status.backupCodesRemaining <= 2 && status.backupCodesRemaining > 0 && (
                <span style={{ color: '#b45309', marginLeft: 6 }}>Running low — regenerate before you exhaust them.</span>
              )}
              {status.backupCodesRemaining === 0 && (
                <span style={{ color: '#dc2626', marginLeft: 6 }}>None left — regenerate now or you'll be locked out if you lose your authenticator.</span>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => { setError(null); setConfirmPassword(''); setConfirmCode(''); setMode('regenerate'); }}
              style={secondaryBtn}
            >
              Regenerate backup codes
            </button>
            <button
              type="button"
              onClick={() => { setError(null); setConfirmPassword(''); setConfirmCode(''); setMode('disable'); }}
              style={dangerBtn}
            >
              Disable 2FA
            </button>
          </div>
        </div>
      )}

      {mode === 'setup' && setupData && (
        <form onSubmit={verifySetup} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <ol style={{ margin: '0 0 4px', paddingLeft: 18, color: 'var(--room-text-body, #334155)', fontSize: 14, lineHeight: 1.6 }}>
            <li>Open your authenticator app (Google Authenticator, 1Password, Authy, Microsoft Authenticator, etc.)</li>
            <li>Scan the QR code below — or paste the manual key into the app's "add key" flow</li>
            <li>Enter the 6-digit code your app shows to confirm setup</li>
          </ol>

          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <img
              src={setupData.qrDataUrl}
              alt="2FA QR code"
              style={{ width: 180, height: 180, borderRadius: 8, border: '1px solid var(--room-border, #e2e8f0)', background: 'var(--room-card, #fff)' }}
            />
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--room-text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Manual key
              </div>
              <div style={{
                marginTop: 4, padding: '8px 10px',
                background: 'var(--room-surface, #f8fafc)', border: '1px solid var(--room-border, #e2e8f0)', borderRadius: 8,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 13, color: 'var(--room-text-strong, #0f172a)', wordBreak: 'break-all',
              }}>
                {setupData.secret}
              </div>
              <div style={{ marginTop: 4, fontSize: 11, color: 'var(--room-text-subtle, #94a3b8)' }}>
                Account: {setupData.account} · Issuer: {setupData.issuer}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 4 }}>
            <label style={inputLabel}>6-digit code from your authenticator</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={setupCode}
              onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              required
              style={{
                ...inputStyle,
                fontSize: 20, letterSpacing: '0.4em', textAlign: 'center',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => { setMode('idle'); setSetupData(null); }} style={secondaryBtn}>
              Cancel
            </button>
            <button type="submit" disabled={setupBusy || setupCode.length !== 6} style={primaryBtn}>
              {setupBusy ? 'Verifying…' : 'Verify and enable'}
            </button>
          </div>
        </form>
      )}

      {mode === 'showBackupCodes' && backupCodes && (
        <div>
          <div style={{
            padding: 12, borderRadius: 10,
            background: '#fef3c7', border: '1px solid #fde68a',
            color: '#92400e', fontSize: 13, marginBottom: 12,
          }}>
            <strong>Save these now. They won't be shown again.</strong>
            {' '}Each code works exactly once. Store them in a password manager — they're your only way back in if you lose your authenticator device.
          </div>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8,
            padding: 12, background: 'var(--room-surface, #f8fafc)', borderRadius: 10, border: '1px solid var(--room-border, #e2e8f0)',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 14, color: 'var(--room-text-strong, #0f172a)',
          }}>
            {backupCodes.map((c, i) => (
              <div key={i} style={{ padding: '4px 8px' }}>{c}</div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(backupCodes.join('\n')).catch(() => {});
              }}
              style={secondaryBtn}
            >
              Copy all
            </button>
            <button type="button" onClick={dismissBackupCodes} style={primaryBtn}>
              I saved them — done
            </button>
          </div>
        </div>
      )}

      {(mode === 'disable' || mode === 'regenerate') && (
        <form onSubmit={(e) => submitConfirm(e, mode)} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ margin: 0, color: 'var(--room-text-body, #475569)', fontSize: 13 }}>
            {mode === 'disable'
              ? 'Confirm with your current password and a 2FA code to disable two-factor authentication. This is a high-impact change and gets logged to your audit trail.'
              : 'Confirm with your current password and a 2FA code to invalidate your existing backup codes and generate new ones.'}
          </p>
          <div>
            <label style={inputLabel}>Current password</label>
            <input
              type="password"
              autoComplete="current-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              style={inputStyle}
            />
          </div>
          <div>
            <label style={inputLabel}>2FA code (or backup code)</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value)}
              required
              placeholder="123456"
              style={{
                ...inputStyle,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                letterSpacing: '0.1em',
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => setMode('idle')} style={secondaryBtn}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={confirmBusy || !confirmPassword || !confirmCode}
              style={mode === 'disable' ? dangerBtn : primaryBtn}
            >
              {confirmBusy
                ? 'Submitting…'
                : (mode === 'disable' ? 'Disable 2FA' : 'Regenerate backup codes')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ─── Inline styles (same look as the rest of the Security tab) ───
const panelStyle = {
  background: 'var(--room-card, #fff)',
  borderRadius: 14,
  border: '1px solid var(--room-border, #e2e8f0)',
  padding: 20,
  marginBottom: 16,
};

const errorStyle = {
  padding: '10px 12px', borderRadius: 8, marginBottom: 12,
  background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)',
  color: '#dc2626', fontSize: 13,
};

const primaryBtn = {
  padding: '10px 18px', borderRadius: 10, border: 'none',
  background: 'linear-gradient(135deg, #0891b2 0%, #0ea5e9 50%, #6366f1 100%)',
  color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  fontFamily: 'inherit',
};

const secondaryBtn = {
  padding: '10px 18px', borderRadius: 10,
  border: '1px solid var(--room-border, #e2e8f0)', background: 'var(--room-card, #fff)',
  color: 'var(--room-text-body, #475569)', fontSize: 14, fontWeight: 500, cursor: 'pointer',
  fontFamily: 'inherit',
};

const dangerBtn = {
  padding: '10px 18px', borderRadius: 10, border: 'none',
  background: '#dc2626', color: '#fff',
  fontSize: 14, fontWeight: 600, cursor: 'pointer',
  fontFamily: 'inherit',
};

const inputLabel = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: 'var(--room-text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: '0.05em',
  marginBottom: 4,
};

const inputStyle = {
  width: '100%', padding: '8px 12px',
  border: '1px solid #cbd5e1', borderRadius: 8,
  fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box',
};
